import { ArrowRight } from 'lucide-react';
import { trackEvent } from '../lib/analytics';

// Keuzeblokken boven het productgrid (plan 4.2): stuur bezoekers op intentie naar de juiste
// landingspagina i.p.v. alleen filteren. Elk blok krijgt een sfeer-visual in het pickleball-court-motief
// (gerepliceerd van de generated product-visuals) voor visuele consistentie.
type Block = { label: string; href: string; hint: string; color: string };

const blocksByCategory: Record<string, Block[]> = {
  overview: [
    { label: 'Paddles', href: '/collecties/paddles/', hint: 'Kies op niveau & speelstijl', color: '#3f7d4f' },
    { label: 'Ballen', href: '/collecties/ballen/', hint: 'Indoor & outdoor', color: '#c26a3a' },
    { label: 'Startersets', href: '/collecties/startersets/', hint: 'Alles in een keer', color: '#4a8f5c' },
    { label: 'Netten', href: '/collecties/netten-en-belijning/', hint: 'Thuis, park of club', color: '#3a86a8' },
    { label: 'Clubs & scholen', href: '/pickleball-clubpakket/', hint: 'Pakket & advies', color: '#7a5aa8' }
  ],
  paddles: [
    { label: 'Voor beginners', href: '/pickleball-paddle-beginners/', hint: 'Controle & groot sweetspot', color: '#4a8f5c' },
    { label: 'Meer controle', href: '/pickleball-paddle-controle/', hint: 'Plaatsing & rust', color: '#3f7d4f' },
    { label: 'Meer power', href: '/pickleball-paddle-power/', hint: 'Snelheid & punch', color: '#c26a3a' },
    { label: 'Meer spin', href: '/pickleball-paddle-spin/', hint: 'Effect & grip', color: '#7a5aa8' },
    { label: 'Lichtgewicht', href: '/pickleball-paddle-lichtgewicht/', hint: 'Wendbaar & armvriendelijk', color: '#3a86a8' }
  ],
  ballen: [
    { label: 'Indoor ballen', href: '/pickleball-indoor-ballen/', hint: 'Zachter, zaalgebruik', color: '#3f7d4f' },
    { label: 'Outdoor ballen', href: '/pickleball-outdoor-ballen/', hint: 'Harder, windbestendig', color: '#c26a3a' },
    { label: 'Indoor vs outdoor', href: '/pickleball-ballen-indoor-outdoor/', hint: 'Welke kies je?', color: '#3a86a8' }
  ],
  startersets: [
    { label: '2 spelers', href: '/pickleball-set-kopen/', hint: 'Samen starten', color: '#4a8f5c' },
    { label: '4 spelers', href: '/pickleball-starterset-kopen/', hint: 'Dubbelspel', color: '#3f7d4f' },
    { label: 'Vereniging / school', href: '/pickleball-materiaal-gymles/', hint: 'Groepen & lessen', color: '#7a5aa8' }
  ],
  'netten-en-belijning': [
    { label: 'Draagbaar net', href: '/pickleball-net-kopen/', hint: 'Thuis & park', color: '#3f7d4f' },
    { label: 'Voor clubs', href: '/pickleball-clubpakket/', hint: 'Stevig & mobiel', color: '#3a86a8' }
  ],
  clubpakketten: [
    { label: 'Verenigingen', href: '/pickleball-voor-verenigingen/', hint: 'Clubpakket', color: '#3f7d4f' },
    { label: 'Scholen', href: '/pickleball-voor-scholen/', hint: 'Gymles', color: '#4a8f5c' },
    { label: 'Bedrijven', href: '/pickleball-bedrijven/', hint: 'Teamactiviteit', color: '#7a5aa8' }
  ]
};

// Court-motief sfeer-visual (gerepliceerd van svgForProduct): baan + lijnen + bal.
function CourtMotif({ color }: { color: string }) {
  return (
    <svg viewBox="0 0 200 120" className="choiceMotif" aria-hidden="true" preserveAspectRatio="xMidYMid slice">
      <rect width="200" height="120" fill={color} />
      <path d="M22 18 H178 V102 H22 Z M100 18 V102 M22 60 H178" fill="none" stroke="rgba(255,255,255,.72)" strokeWidth="3" />
      <path d="M52 18 V102 M148 18 V102" stroke="rgba(255,255,255,.32)" strokeWidth="2" />
      <circle cx="150" cy="34" r="7.5" fill="none" stroke="rgba(255,255,255,.9)" strokeWidth="3" />
      <circle cx="150" cy="34" r="2.5" fill="rgba(255,255,255,.9)" />
    </svg>
  );
}

export function CategoryChoiceBlocks({ category }: { category: string }) {
  const blocks = blocksByCategory[category];
  if (!blocks || blocks.length === 0) return null;
  return (
    <section className="section choiceBlocks" aria-label="Snel kiezen op intentie">
      <div className="sectionHeading compactHeading">
        <p className="eyebrow">Snel kiezen</p>
        <h2>Waar zoek je op?</h2>
      </div>
      <div className="choiceBlockGrid">
        {blocks.map((b) => (
          <a
            key={b.href}
            className="choiceBlock"
            href={b.href}
            onClick={() => trackEvent({ event: 'cta_click', location: 'category_choice_block', label: b.label, routeFamily: 'products', placement: category })}
          >
            <CourtMotif color={b.color} />
            <span className="choiceBlockBody">
              <strong>{b.label}</strong>
              <small>{b.hint}</small>
            </span>
            <ArrowRight size={16} className="choiceBlockArrow" aria-hidden="true" />
          </a>
        ))}
      </div>
    </section>
  );
}
